import { Box, BoxProps, Flex, Text, TextProps } from '@chakra-ui/react'
import { ReactNode } from 'react'

import { Icon } from '../Icon'

export type DetailFieldLayout = 'stacked' | 'inline'

/**
 * Valor ausente. Havia `-` (35 sites), `—` (6), `Sem resposta` (19) e o campo simplesmente
 * sumindo da tela. O `-` é o mais usado; `Sem resposta` continua certo nas telas de resposta
 * de formulário, onde "não respondeu" é diferente de "não há dado", e entra por `emptyValue`.
 */
const DEFAULT_EMPTY_VALUE = '-'

export interface IDetailFieldProps extends Omit<BoxProps, 'children' | 'title'> {
  /** Rótulo em pt-BR. Sem dois-pontos no fim — a mesma convenção do `FormField`. */
  label: ReactNode
  /** O valor já formatado (`formatCurrency`, `formatApiDate`). Vazio vira `emptyValue`. */
  value?: ReactNode
  /** Conteúdo no lugar do `value`, para o campo cujo valor é um selo, um link, uma lista. */
  children?: ReactNode
  /**
   * Forma do par, não medida do Chakra — a mesma ideia do `size` do `FormModal`.
   * `stacked` (padrão) é o rótulo acima do valor, para uma ficha ou uma grade de campos;
   * `inline` põe os dois na mesma linha, com o valor à direita, para o resumo de um pedido.
   */
  layout?: DetailFieldLayout
  /** Classe do LineIcons (`lni lni-calendar`) — vai dentro do ladrilho de 32px. Só em `stacked`. */
  icon?: string
  /** Texto exibido quando `value` é vazio. Padrão `-`. */
  emptyValue?: ReactNode
}

/**
 * Par rótulo/valor das páginas e fichas de detalhe — o irmão somente-leitura do `<FormField>`.
 *
 * Estava escrito **nove vezes, em cinco formas**: o `InfoRow({icon,label,value})` duplicado
 * byte a byte entre `ContestDetailPage` e `EventDetailPage`, o `DetailRow({label,value})`
 * duplicado entre `EventOrdersPage` e `EventTransactionsPage`, mais o `InfoRow` do
 * `ContestsSendEmailsModal`, o `Info` do `StudyCreditViewModal`, o `Field` do
 * `ResponsibleDetailsModal`, o `Field` do `PartnerContactModal` e o `Row` do
 * `SalesReceiptViewModal`. As cópias já divergiam no rótulo (`xs` em `gray.400`, `xs` em
 * `gray.500`, `sm` em `gray.500`, `bold` em `gray.600`) e no valor ausente.
 *
 * **A sexta forma era a errada, e é a que este componente existe para tirar do caminho:**
 * `RefundsViewPage`, `PersonnelMovementRequestViewPage` e `ExperienceFeedbackViewPage`
 * embrulhavam um valor de leitura num `FormControl`/`FormField`. Um `FormControl` sem controle
 * não associa nada: o `htmlFor` do rótulo aponta para um input que não existe, e o
 * `isInvalid`/`isRequired` que ele propaga não tem para onde ir. No `ExperienceFeedbackViewPage`
 * cada rótulo ainda era um `<Heading as="h3">` dentro do `<FormLabel>`, o que punha vinte `h3`
 * no outline de uma página cujo conteúdo é uma ficha.
 *
 * Props de layout (`flex`, `gridColumn`, `mb`) vão no próprio campo — a mesma divisão do
 * `FormField`, e o `<Box>`/`<GridItem>` que embrulhava o par some com elas.
 */
export default function DetailField({
  label,
  value,
  children,
  layout = 'stacked',
  icon,
  emptyValue = DEFAULT_EMPTY_VALUE,
  ...rest
}: IDetailFieldProps) {
  const isEmpty = children == null && (value == null || value === '')
  const content = children ?? (isEmpty ? emptyValue : value)

  // A grafia do par mora no `theme.ts` (`variant="label"`/`variant="value"`), porque ela
  // também é a do par escrito fora de uma ficha — ter a definição aqui era o que deixava as
  // quatro grafias soltas do painel sem um lugar para convergir.
  const labelNode = (props?: TextProps) => (
    <Text as="dt" variant="label" {...props}>
      {label}
    </Text>
  )

  // `gray.400` no vazio: o campo continua na tela em vez de sumir — o que falta cadastrar é
  // informação útil —, mas sem competir com os campos preenchidos. É o que o
  // `PartnerContactModal` já fazia, e o comentário dele dizia por quê.
  //
  // `ml={0}`: o `<dd>` nasce com `margin-inline-start: 40px` no browser, e sem isso todo valor
  // da ficha ficaria indentado.
  const valueNode = (props?: TextProps) => (
    <Text as="dd" variant="value" ml={0} {...(isEmpty ? { color: 'gray.400' } : {})} wordBreak="break-word" {...props}>
      {content}
    </Text>
  )

  // O `<dl>` é sempre o pai direto do par: ele só aceita `dt`/`dd` (ou um `div` agrupando os
  // dois), então na variante com ícone é o bloco de texto que vira `dl`, não a linha inteira.
  if (layout === 'inline') {
    return (
      <Flex as="dl" justify="space-between" align="baseline" gap={4} {...rest}>
        {labelNode({ flexShrink: 0 })}
        {valueNode({ textAlign: 'right', minW: 0 })}
      </Flex>
    )
  }

  if (icon) {
    return (
      <Flex align="center" gap={3} {...rest}>
        <Flex
          w="32px"
          h="32px"
          borderRadius="lg"
          bg="gray.50"
          color="gray.500"
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon name={icon} size="md" />
        </Flex>
        <Box as="dl" minW={0}>
          {labelNode()}
          {valueNode()}
        </Box>
      </Flex>
    )
  }

  return (
    <Box as="dl" minW={0} {...rest}>
      {labelNode()}
      {valueNode()}
    </Box>
  )
}
